import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from '@mui/material';
import LoadingButton from '@/components/ui/LoadingButton/LoadingButton.tsx';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import BackButton from '@/components/ui/BackButton/BackButton.tsx';
import LoaderWrapper from '@/components/ui/LoaderWrapper/LoaderWrapper.tsx';
import EditIcon from '@mui/icons-material/Edit';
import useRestaurants from '@/api/useRestaurants.ts';
import { schema } from '@/components/modals/Restaurant/AddRestaurantModal.tsx';
import type { Schema } from '@/components/modals/Restaurant/AddRestaurantModal.tsx';
import useRestaurantEdit from '@/api/useRestaurantEdit.ts';
import { MuiTelInput } from 'mui-tel-input';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { dayjsToTimeString, timeStringToDayjs } from '@/utils/dayjsToTimeString.ts';
import { AvailableTimezones } from '@/_constants/AvailableTimezones.ts';

type EditRestaurantModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  restaurantId: number;
};
function EditRestaurantModal({ isOpen, onClose, onConfirm, restaurantId }: EditRestaurantModalProps) {
  const { t } = useTranslation();
  const {
    data: currentData,
    isLoading: isLoadingCurrentData,
    isSuccess,
  } = useRestaurants(true, {
    restaurant_id: restaurantId,
  });
  const {
    mutateAsync: editRestaurant,
    isError: isErrorEditRestaurant,
    isPending: isLoadingEditRestaurant,
    isSuccess: isSuccessEditRestaurant,
    error: errorEditRestaurant,
  } = useRestaurantEdit();

  const { handleSubmit, control, setValue } = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      address: '',
      phone: '',
      redirect_phone: '',
      timezone: 'UTC',
      weekday_hours_from: null,
      weekday_hours_to: null,
      weekend_hours_from: null,
      weekend_hours_to: null,
      bot_enabled: false,
    },
  });

  const handleUpdateRestaurant = (data: Schema) => {
    void (async () => {
      try {
        if (isLoadingEditRestaurant || !currentData || !currentData[0]) {
          return;
        }

        await editRestaurant({
          ...data,
          id: restaurantId,
        });

        if (onConfirm) {
          onConfirm();
        }

        onClose();
      } catch (error) {
        console.error('Error editing restaurant: ', error);
      }
    })();
  };

  useEffect(() => {
    if (isSuccess && currentData[0]) {
      const {
        name,
        address,
        phone,
        redirect_phone,
        timezone,
        weekday_hours_from,
        weekday_hours_to,
        weekend_hours_from,
        weekend_hours_to,
        bot_enabled,
      } = currentData[0];
      setValue('name', name ?? '');
      setValue('address', address ?? '');
      // @ts-expect-error default value is not correct number
      setValue('phone', phone ?? '');
      // @ts-expect-error default value is not correct number
      setValue('redirect_phone', redirect_phone ?? '');
      setValue('timezone', timezone ?? 'UTC');
      setValue('weekday_hours_from', weekday_hours_from ?? null);
      setValue('weekday_hours_to', weekday_hours_to ?? null);
      setValue('weekend_hours_from', weekend_hours_from ?? null);
      setValue('weekend_hours_to', weekend_hours_to ?? null);
      setValue('bot_enabled', bot_enabled ?? false);
    }
  }, [currentData, isLoadingCurrentData, isSuccess, setValue]);

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <form className="modal-form" onSubmit={handleSubmit(handleUpdateRestaurant)}>
        <DialogTitle>{t('modals.editRestaurantModal.title')}</DialogTitle>
        <DialogContent>
          <LoaderWrapper isLoading={isLoadingCurrentData}>
            <div className="pt-2 flex flex-col gap-4">
              <div className="flex flex-col gap-2 empty:hidden">
                {isSuccessEditRestaurant && (
                  <Alert severity="success">{t('modals.editRestaurantModal.successMessage')}</Alert>
                )}
                {isErrorEditRestaurant && <Alert severity="error">{errorEditRestaurant?.message as string}</Alert>}
              </div>
              <div className="flex flex-col gap-4 w-full">
                <Controller
                  control={control}
                  name="bot_enabled"
                  render={({ field: { onChange, value } }) => (
                    <FormControlLabel
                      control={<Switch value={value} checked={value} onChange={onChange} />}
                      label={t('label.bot_enabled')}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="name"
                  render={({ field: { onChange, value }, formState: { errors } }) => (
                    <TextField
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      value={value}
                      onChange={onChange}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                      variant="outlined"
                      label={t('label.name')}
                      placeholder={t('placeholder.name')}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="address"
                  render={({ field: { onChange, value }, formState: { errors } }) => (
                    <TextField
                      error={!!errors.address}
                      helperText={errors.address?.message}
                      value={value}
                      onChange={onChange}
                      multiline
                      minRows={2}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                      variant="outlined"
                      label={t('label.address')}
                      placeholder={t('placeholder.address')}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="phone"
                  render={({ field: { onChange, value }, formState: { errors } }) => (
                    <MuiTelInput
                      error={!!errors.phone}
                      helperText={errors.phone?.message}
                      value={value}
                      onChange={onChange}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                      variant="outlined"
                      label={t('label.restaurant_phone')}
                      placeholder={t('placeholder.restaurant_phone')}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="redirect_phone"
                  render={({ field: { onChange, value }, formState: { errors } }) => (
                    <MuiTelInput
                      error={!!errors.redirect_phone}
                      helperText={errors.redirect_phone?.message}
                      value={value}
                      onChange={onChange}
                      slotProps={{
                        inputLabel: {
                          shrink: true,
                        },
                      }}
                      variant="outlined"
                      label={t('label.redirect_phone')}
                      placeholder={t('placeholder.redirect_phone')}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="timezone"
                  render={({ field: { onChange, value }, formState: { errors } }) => (
                    <FormControl error={!!errors.timezone}>
                      <InputLabel shrink id="timezone">
                        {t('label.timezone')}
                      </InputLabel>
                      <Select
                        value={value}
                        onChange={(event) => onChange(`${event.target.value}`)}
                        displayEmpty
                        variant="outlined"
                        label={t('label.timezone')}
                        labelId="timezone"
                        renderValue={(currentValue) =>
                          currentValue == '' ? (
                            <span className="text-[#a1a2a1]">{t('placeholder.timezone')}</span>
                          ) : (
                            AvailableTimezones?.find((timezone) => timezone === currentValue)
                          )
                        }
                      >
                        {AvailableTimezones?.map((timezone) => (
                          <MenuItem key={`timezone_${timezone}`} value={timezone}>
                            {timezone}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.timezone?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
                <div className="grid grid-cols-2 gap-4">
                  <Controller
                    control={control}
                    name="weekday_hours_from"
                    render={({ field: { onChange, value }, formState: { errors } }) => (
                      <TimePicker
                        ampm={false}
                        className="col-span-2 lg:col-span-1"
                        views={['hours', 'minutes']}
                        value={value ? timeStringToDayjs(value) : null}
                        onChange={(newValue) => {
                          onChange(newValue ? dayjsToTimeString(newValue) : null);
                        }}
                        slotProps={{
                          textField: {
                            error: !!errors.weekday_hours_from,
                            helperText: errors.weekday_hours_from?.message,
                            variant: 'outlined',
                            placeholder: t('placeholder.weekday_hours_from'),
                            label: t('label.weekday_hours_from'),
                            InputLabelProps: {
                              shrink: true,
                            },
                          },
                        }}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="weekday_hours_to"
                    render={({ field: { onChange, value }, formState: { errors } }) => (
                      <TimePicker
                        ampm={false}
                        className="col-span-2 lg:col-span-1"
                        views={['hours', 'minutes']}
                        value={value ? timeStringToDayjs(value) : null}
                        onChange={(newValue) => {
                          onChange(newValue ? dayjsToTimeString(newValue) : null);
                        }}
                        slotProps={{
                          textField: {
                            error: !!errors.weekday_hours_to,
                            helperText: errors.weekday_hours_to?.message,
                            variant: 'outlined',
                            placeholder: t('placeholder.weekday_hours_to'),
                            label: t('label.weekday_hours_to'),
                            InputLabelProps: {
                              shrink: true,
                            },
                          },
                        }}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="weekend_hours_from"
                    render={({ field: { onChange, value }, formState: { errors } }) => (
                      <TimePicker
                        ampm={false}
                        className="col-span-2 lg:col-span-1"
                        views={['hours', 'minutes']}
                        value={value ? timeStringToDayjs(value) : null}
                        onChange={(newValue) => {
                          onChange(newValue ? dayjsToTimeString(newValue) : null);
                        }}
                        slotProps={{
                          textField: {
                            error: !!errors.weekend_hours_from,
                            helperText: errors.weekend_hours_from?.message,
                            variant: 'outlined',
                            placeholder: t('placeholder.weekend_hours_from'),
                            label: t('label.weekend_hours_from'),
                            InputLabelProps: {
                              shrink: true,
                            },
                          },
                        }}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="weekend_hours_to"
                    render={({ field: { onChange, value }, formState: { errors } }) => (
                      <TimePicker
                        ampm={false}
                        className="col-span-2 lg:col-span-1"
                        views={['hours', 'minutes']}
                        value={value ? timeStringToDayjs(value) : null}
                        onChange={(newValue) => {
                          onChange(newValue ? dayjsToTimeString(newValue) : null);
                        }}
                        slotProps={{
                          textField: {
                            error: !!errors.weekend_hours_to,
                            helperText: errors.weekend_hours_to?.message,
                            variant: 'outlined',
                            placeholder: t('placeholder.weekend_hours_to'),
                            label: t('label.weekend_hours_to'),
                            InputLabelProps: {
                              shrink: true,
                            },
                          },
                        }}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </LoaderWrapper>
        </DialogContent>
        <DialogActions>
          <div className="flex flex-wrap grow p-4 pt-0 justify-between">
            <BackButton onClick={onClose} autoFocus />
            <LoadingButton
              startIcon={<EditIcon />}
              color="primary"
              variant="contained"
              loading={isLoadingEditRestaurant}
              type="submit"
            >
              {t('button.update')}
            </LoadingButton>
          </div>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default EditRestaurantModal;
